import React, { useState, useEffect } from 'react';
import { appVersionAPI } from '../services/api';
import { AppVersion } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Edit, Delete, Refresh, Add, SystemUpdate } from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const AppVersions: React.FC = () => {
  const [appVersions, setAppVersions] = useState<AppVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    minSupportedVersion: '',
    latestVersion: '',
    storeUrlIos: '',
    storeUrlAndroid: '',
    message: '',
    platform: 'both' as 'ios' | 'android' | 'both',
    isActive: true
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchAppVersions();
  }, []);

  const fetchAppVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await appVersionAPI.getAll();
      setAppVersions(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load app versions');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError('');
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    setForm({ ...form, [name]: e.target.value });
    setFormError('');
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.checked });
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setEditingId(null);
    setForm({
      minSupportedVersion: '',
      latestVersion: '',
      storeUrlIos: '',
      storeUrlAndroid: '',
      message: '',
      platform: 'both',
      isActive: true
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (appVersion: AppVersion) => {
    setIsEditMode(true);
    setEditingId(appVersion.id!);
    setForm({
      minSupportedVersion: appVersion.minSupportedVersion,
      latestVersion: appVersion.latestVersion,
      storeUrlIos: appVersion.storeUrlIos,
      storeUrlAndroid: appVersion.storeUrlAndroid,
      message: appVersion.message,
      platform: appVersion.platform,
      isActive: appVersion.isActive
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError('');
  };

  const validateForm = (): boolean => {
    if (!form.minSupportedVersion || !form.latestVersion) {
      setFormError('Min Supported Version and Latest Version are required');
      return false;
    }
    if (!form.storeUrlIos && !form.storeUrlAndroid) {
      setFormError('At least one store URL is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (isEditMode && editingId) {
        await appVersionAPI.update(editingId, form);
        setSuccessMsg('App Version updated successfully');
      } else {
        await appVersionAPI.create(form);
        setSuccessMsg('App Version created successfully');
      }
      await fetchAppVersions();
      closeModal();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to save app version');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this app version?')) return;
    
    setDeletingId(id);
    try {
      await appVersionAPI.delete(id);
      setSuccessMsg('App Version deleted successfully');
      await fetchAppVersions();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete app version');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (appVersion: AppVersion) => {
    try {
      if (appVersion.isActive) {
        await appVersionAPI.deactivate(appVersion.id!);
        setSuccessMsg('App Version deactivated successfully');
      } else {
        await appVersionAPI.activate(appVersion.id!);
        setSuccessMsg('App Version activated successfully');
      }
      await fetchAppVersions();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to update app version status');
    }
  };

  const getPlatformColor = (platform: string): 'primary' | 'secondary' | 'default' => {
    switch (platform) {
      case 'ios':
        return 'primary';
      case 'android':
        return 'secondary';
      case 'both':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (isActive: boolean): 'success' | 'default' => {
    return isActive ? 'success' : 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          App Version Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openAddModal}
          sx={{ borderRadius: 2 }}
        >
          Add App Version
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Platform</TableCell>
                <TableCell>Min Version</TableCell>
                <TableCell>Latest Version</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appVersions.map((appVersion) => (
                <TableRow key={appVersion.id} hover>
                  <TableCell>
                    <Chip 
                      label={appVersion.platform} 
                      color={getPlatformColor(appVersion.platform)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={appVersion.isActive ? 'Active' : 'Inactive'} 
                      color={getStatusColor(appVersion.isActive)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={appVersion.minSupportedVersion}
                      color="error"
                      size="small"
                      icon={<SystemUpdate />}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={appVersion.latestVersion}
                      color="info"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {appVersion.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {appVersion.lastUpdated ? new Date(appVersion.lastUpdated).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEditModal(appVersion)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={appVersion.isActive ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleStatus(appVersion)}
                          color={appVersion.isActive ? 'warning' : 'success'}
                        >
                          <Switch
                            checked={appVersion.isActive}
                            onChange={() => handleToggleStatus(appVersion)}
                            size="small"
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(appVersion.id!)}
                          color="error"
                          disabled={deletingId === appVersion.id}
                        >
                          {deletingId === appVersion.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Delete />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit App Version' : 'Add New App Version'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  name="platform"
                  value={form.platform}
                  onChange={handleSelectInput}
                  label="Platform"
                >
                  <MenuItem value="ios">iOS</MenuItem>
                  <MenuItem value="android">Android</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleSwitchChange}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Supported Version"
                name="minSupportedVersion"
                value={form.minSupportedVersion}
                onChange={handleInput}
                required
                placeholder="e.g., 1.0.0"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Latest Version"
                name="latestVersion"
                value={form.latestVersion}
                onChange={handleInput}
                required
                placeholder="e.g., 2.1.0"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="iOS Store URL"
                name="storeUrlIos"
                value={form.storeUrlIos}
                onChange={handleInput}
                placeholder="https://apps.apple.com/..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Android Store URL"
                name="storeUrlAndroid"
                value={form.storeUrlAndroid}
                onChange={handleInput}
                placeholder="https://play.google.com/..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Update Message"
                name="message"
                value={form.message}
                onChange={handleInput}
                multiline
                rows={3}
                placeholder="Enter update message for users..."
              />
            </Grid>
          </Grid>
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppVersions;
